import React, { useState, useEffect } from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import { Line } from 'react-chartjs-2';
import './RatingHistoryChart.css';
import api from '../../services/api';

// 注册必要的Chart.js组件
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

/**
 * 段位颜色配置
 * 为不同的段位定义对应的颜色
 */
const tierColors = {
  'S+': '#FF4D4F',
  'S': '#FF7A45',
  'A+': '#FFA940',
  'A': '#FFB73F',
  'B+': '#FFD666',
  'B': '#FFF566',
  'C+': '#95DE64',
  'C': '#5CDBD3',
  'D+': '#69C0FF',
  'D': '#85A5FF',
  '未分级': '#D9D9D9'
};

const RatingHistoryChart = ({ playerId, zone }) => {
  const [history, setHistory] = useState(null);
  const [type, setType] = useState('singles');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/api/${zone}/players/${playerId}/rating-history`);
        const data = response.data;
        console.log('API返回数据:', data);
        
        if (data.success && data.data) {
          setHistory(data.data);
        } else {
          setError(data.message || '获取积分历史失败');
        }
      } catch (err) {
        console.error('获取积分历史失败:', err);
        setError('获取积分历史失败');
      } finally {
        setLoading(false);
      }
    };

    if (playerId) {
      fetchHistory();
    }
  }, [playerId, zone]);

  // 加载状态处理
  if (loading) return <div className="loading">加载中...</div>;
  if (error) return <div className="error">{error}</div>;
  if (!history) return <div className="no-data">暂无数据</div>;

  // 获取当前类型的数据
  const currentData = history[type];
  if (!currentData) return <div className="no-data">暂无{type === 'singles' ? '单打' : '双打'}数据</div>;

  // 解构数据
  const { history: matchHistory = [], snapshots = [] } = currentData;
  console.log('原始数据:', { matchHistory, snapshots });

  // 按日期汇总数据（取每天最后一场比赛的积分）
  let dailyRatings = {};
  matchHistory.forEach(match => {
    const dateStr = new Date(match.date).toISOString().split('T')[0]; // 提取日期部分
    dailyRatings[dateStr] = {
      date: new Date(match.date),
      rating: parseFloat(match.rating) || 1500,
      ratingChange: parseFloat(match.ratingChange) || 0,
      result: match.result || '',
      scores: match.scores || '',
      opponent: match.opponent || '',
      partner: match.partner,
      opponents: match.opponents
    };
  });

  // 将日期汇总数据转换为数组
  const dataPoints = Object.values(dailyRatings)
    .map(point => ({
      ...point,
      tier: snapshots.length > 0 ? snapshots[0].tier : 'NEW',
      wins: snapshots.length > 0 ? snapshots[0].wins : 0,
      losses: snapshots.length > 0 ? snapshots[0].losses : 0,
      winRate: snapshots.length > 0 ? snapshots[0].winRate : 0,
      totalMatches: snapshots.length > 0 ? snapshots[0].totalMatches : 0
    }))
    .sort((a, b) => a.date - b.date);

  // 如果没有数据，添加默认数据点
  if (dataPoints.length === 0) {
    const initialRating = currentData.currentRating || 1500;
    dataPoints.push({
      date: new Date(),
      rating: initialRating,
      ratingChange: 0,
      result: '',
      scores: '',
      opponent: '',
      tier: 'NEW',
      wins: 0,
      losses: 0,
      winRate: 0,
      totalMatches: 0
    });
  }

  // 添加初始积分点作为第一个数据点（如果数据足够）
  if (dataPoints.length > 0 && currentData.currentRating) {
    // 添加起始点（初始积分1500）
    const firstPoint = {
      date: new Date(dataPoints[0].date),
      rating: 1500,
      ratingChange: 0,
      result: '',
      scores: '',
      opponent: '',
      tier: 'NEW',
      wins: 0,
      losses: 0,
      winRate: 0,
      totalMatches: 0
    };
    // 将日期提前7天
    firstPoint.date.setDate(firstPoint.date.getDate() - 7);
    dataPoints.unshift(firstPoint);
  }

  console.log('处理后的数据点:', dataPoints);

  // 图表数据配置 - 修改为Chart.js v3兼容格式
  const chartData = {
    labels: dataPoints.map(point => 
      point.date.toLocaleDateString('zh-CN', {
        month: '2-digit',
        day: '2-digit'
      })
    ),
    datasets: [
      {
        label: '积分',
        data: dataPoints.map(point => point.rating),
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.5)',
        pointBackgroundColor: point => point.result === '胜' ? 'rgb(82, 196, 26)' : 'rgb(255, 77, 79)',
        pointRadius: 5,
        pointHoverRadius: 8,
        tension: 0.1,
        fill: false
      }
    ]
  };

  // 图表选项配置 - 修改为Chart.js v3兼容格式
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        enabled: true,
        mode: 'index',
        intersect: false,
        callbacks: {
          title: (tooltipItems) => {
            const index = tooltipItems[0].dataIndex;
            const point = dataPoints[index];
            return point.date.toLocaleDateString('zh-CN', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit'
            });
          },
          label: (tooltipItem) => {
            const index = tooltipItem.dataIndex;
            const point = dataPoints[index];
            const labels = [`积分: ${point.rating}`];
            
            if (point.ratingChange) {
              const sign = point.ratingChange > 0 ? '+' : '';
              labels.push(`变化: ${sign}${point.ratingChange}`);
            }
            
            if (point.result) labels.push(`结果: ${point.result}`);
            if (point.scores) labels.push(`比分: ${point.scores}`);
            if (point.opponent) labels.push(`对手: ${point.opponent}`);
            if (point.partner) labels.push(`搭档: ${point.partner}`);
            if (point.tier) labels.push(`段位: ${point.tier}`);
            
            return labels;
          }
        }
      }
    },
    scales: {
      x: {
        display: true,
        title: {
          display: false
        },
        grid: {
          display: false
        },
        ticks: {
          maxRotation: 45,
          minRotation: 45,
          font: {
            size: 10
          }
        }
      },
      y: {
        display: true,
        title: {
          display: false
        },
        min: 1200,
        max: 1800,
        ticks: {
          stepSize: 100,
          callback: function(value) {
            return value;
          },
          font: {
            size: 10
          }
        },
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.05)'
        }
      }
    }
  };

  return (
    <div className="rating-history-chart">
      <div className="chart-header">
        <h3>积分走势</h3>
        <div className="type-toggle">
          <button 
            className={type === 'singles' ? 'active' : ''} 
            onClick={() => setType('singles')}
          >
            单打
          </button>
          <button 
            className={type === 'doubles' ? 'active' : ''} 
            onClick={() => setType('doubles')}
          >
            双打
          </button>
        </div>
      </div>
      <div className="chart-container" style={{ height: '280px' }}>
        <Line 
          data={chartData} 
          options={chartOptions}
          redraw={true}
        />
      </div>
    </div>
  );
};

export default RatingHistoryChart; 